import { workflowActionAllowedForAnyRole } from '../services/workflowRuntimeService';
import { isInitialReviewPhase } from './phaseOperatorAssignmentRoles';
import {
  canModifyCaseFilesDuringSubmission,
  canEditPrescriptionDuringSubmission,
} from './casePrescriptionEditPolicy';

export const MODIFY_CASE_FILES_ACTION_KEY = 'modify_case_files';

function normalizeText(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Phase « Révision » (groupe 1 ordre 2 ou libellé). */
export function isReviewPhase(phase) {
  if (!phase) return false;
  if (isInitialReviewPhase(phase)) return true;
  const n = normalizeText(phase.name);
  return n.includes('revision');
}

/** Jalon « contrôle qualité » (libellé template). */
export function isQualityControlMilestoneName(name) {
  const n = normalizeText(name);
  return n.includes('qualit') && (n.includes('controle') || n.includes('control'));
}

/**
 * Jalon contrôle qualité actif dans une phase révision en cours.
 * @param {object} phase
 */
export function getActiveReviewQualityControlMilestone(phase) {
  if (!phase || phase.status !== 'in_progress' || !isReviewPhase(phase)) {
    return null;
  }

  const milestones = [...(phase.milestones || [])].sort(
    (a, b) => (a.milestone_order ?? 0) - (b.milestone_order ?? 0),
  );

  const qcIncomplete = milestones.filter(
    (m) => isQualityControlMilestoneName(m.name) && m.completed !== true,
  );
  if (qcIncomplete.length > 0) {
    return qcIncomplete[0];
  }

  const firstIncomplete = milestones.find((m) => m.completed !== true);
  if (firstIncomplete && isQualityControlMilestoneName(firstIncomplete.name)) {
    return firstIncomplete;
  }

  return null;
}

function unionAllowedRolesFromActions(templateActions = []) {
  const roles = new Set();
  for (const row of templateActions) {
    for (const r of row?.allowed_role_types || []) {
      if (typeof r === 'string' && r.trim()) roles.add(r.trim());
    }
  }
  return [...roles];
}

/**
 * Fichiers modifiables pendant révision + jalon contrôle qualité :
 * rôles = ceux du workflow sur ce jalon (action modify_case_files ou union des actions).
 */
export function canModifyCaseFilesDuringReviewQualityControl({
  userRole,
  userRoleTypes = [],
  timelinePhaseItems = [],
  templateActionsByTemplateId = {},
}) {
  const roles = userRoleTypes?.length
    ? userRoleTypes
    : userRole
      ? [userRole]
      : [];

  if (!roles.length) return false;

  for (const phase of timelinePhaseItems || []) {
    const milestone = getActiveReviewQualityControlMilestone(phase);
    if (!milestone) continue;

    const templateId = milestone.milestone_template_id;
    const actions = templateId ? templateActionsByTemplateId[templateId] || [] : [];

    const fileAction = actions.find((a) => a.action_key === MODIFY_CASE_FILES_ACTION_KEY);
    if (fileAction) {
      return workflowActionAllowedForAnyRole(roles, fileAction.allowed_role_types || []);
    }

    const allowedFromActions = unionAllowedRolesFromActions(actions);
    if (allowedFromActions.length > 0) {
      return workflowActionAllowedForAnyRole(roles, allowedFromActions);
    }

    return workflowActionAllowedForAnyRole(roles, [
      'administrator',
      'agent_administratif',
      'dentist_supplier',
      'operator_qualite',
    ]);
  }

  return false;
}

export function canModifyCaseFiles(ctx) {
  return (
    canModifyCaseFilesDuringSubmission(ctx) ||
    canModifyCaseFilesDuringReviewQualityControl(ctx)
  );
}

export function canEditPrescriptionOnFilesTab(ctx) {
  return (
    canEditPrescriptionDuringSubmission(ctx) ||
    canModifyCaseFilesDuringReviewQualityControl(ctx)
  );
}
